import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../lib/apiClient';

export type CoAuthorOption = { value: string; label: string };

type CachePayload = {
  version: 1;
  savedAt: number;
  options: CoAuthorOption[];
};

type HookParams = {
  loginId?: string | number | null;
  departmentId?: string | number | null;
  teamIds?: Array<string | number | null | undefined>;
  officialOptions?: Array<CoAuthorOption | string | number | null | undefined>;
};

const CACHE_VERSION = 1;
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_OPTIONS = 300;
const CACHE_PREFIX = 'coAuthorRespondents';
const RESPONDENT_SPLIT = /\s*[,\u3001\uFF0C]\s*|\s*[\r\n]+\s*/;

const memoryCache = new Map<string, CachePayload>();
const inFlight = new Map<string, Promise<CoAuthorOption[]>>();
const listeners = new Map<string, Set<() => void>>();

const norm = (s: any) => String(s ?? '').normalize('NFKC').trim();
export const coAuthorNameKey = (s: any) => norm(s).replace(/\s+/g, '');

const getStorage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

const toStoredKey = (cacheKey: string) => `${CACHE_PREFIX}:${cacheKey}`;

const normalizeTeamIds = (teamIds?: HookParams['teamIds']): string[] =>
  Array.from(
    new Set(
      (teamIds ?? [])
        .map((id) => norm(id))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, 'ja'))
    )
  );

const makeCacheKey = (params: HookParams): string => {
  const loginKey = norm(params.loginId).toLowerCase() || 'anonymous';
  const deptKey = norm(params.departmentId) || 'none';
  const teamKey = normalizeTeamIds(params.teamIds).join(',') || 'none';
  return `v${CACHE_VERSION}:u=${loginKey}:d=${deptKey}:t=${teamKey}`;
};

const optionFromName = (name: any): CoAuthorOption | null => {
  const label = norm(name);
  if (!label) return null;
  return { value: label, label };
};

const optionFromAny = (value: any): CoAuthorOption | null => {
  if (value == null) return null;

  if (typeof value === 'string' || typeof value === 'number') {
    return optionFromName(value);
  }

  if (typeof value !== 'object') return null;

  const label = norm(value?.label ?? value?.name ?? value?.text ?? value?.value ?? '');
  const rawValue = norm(value?.value ?? label);
  if (label || rawValue) {
    return {
      value: rawValue || label,
      label: label || rawValue,
    };
  }

  const lastName = norm(
    value?.last_name ??
      value?.lastName ??
      value?.family_name ??
      value?.familyName ??
      ''
  );
  const firstName = norm(
    value?.first_name ??
      value?.firstName ??
      value?.given_name ??
      value?.givenName ??
      ''
  );
  return optionFromName([lastName, firstName].filter(Boolean).join(' '));
};

export const mergeCoAuthorOptionsByLabel = (
  ...groups: Array<Array<CoAuthorOption | string | number | null | undefined>>
): CoAuthorOption[] => {
  const out: CoAuthorOption[] = [];
  const seen = new Set<string>();

  groups.forEach((group) => {
    (group ?? []).forEach((item) => {
      const opt = optionFromAny(item);
      if (!opt) return;

      const label = norm(opt.label ?? opt.value);
      const value = norm(opt.value ?? label);
      const key = coAuthorNameKey(label || value);
      if (!key || seen.has(key)) return;

      seen.add(key);
      out.push({ value: value || label, label: label || value });
    });
  });

  return out;
};

const limitOptions = (options: CoAuthorOption[]): CoAuthorOption[] =>
  mergeCoAuthorOptionsByLabel(options).slice(0, MAX_OPTIONS);

const parseCache = (raw: string | null): CachePayload | null => {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as CachePayload;
    if (parsed?.version !== CACHE_VERSION) return null;
    if (!Number.isFinite(Number(parsed.savedAt))) return null;
    return {
      version: CACHE_VERSION,
      savedAt: Number(parsed.savedAt),
      options: limitOptions(parsed.options ?? []),
    };
  } catch {
    return null;
  }
};

const readCache = (cacheKey: string): CachePayload | null => {
  const cached = memoryCache.get(cacheKey);
  if (cached) return cached;

  const storage = getStorage();
  let parsed: CachePayload | null = null;
  if (storage) {
    try {
      parsed = parseCache(storage.getItem(toStoredKey(cacheKey)));
    } catch {
      parsed = null;
    }
  }
  if (parsed) memoryCache.set(cacheKey, parsed);
  return parsed;
};

const notify = (cacheKey: string) => {
  listeners.get(cacheKey)?.forEach((listener) => listener());
};

const writeCache = (cacheKey: string, options: CoAuthorOption[], savedAt = Date.now()) => {
  const payload: CachePayload = {
    version: CACHE_VERSION,
    savedAt,
    options: limitOptions(options),
  };

  memoryCache.set(cacheKey, payload);

  const storage = getStorage();
  if (storage) {
    try {
      storage.setItem(toStoredKey(cacheKey), JSON.stringify(payload));
    } catch {
      // Storage quota/private mode failures should not block the UI.
    }
  }

  notify(cacheKey);
};

const subscribe = (cacheKey: string, listener: () => void) => {
  const set = listeners.get(cacheKey) ?? new Set<() => void>();
  set.add(listener);
  listeners.set(cacheKey, set);

  return () => {
    set.delete(listener);
    if (set.size === 0) listeners.delete(cacheKey);
  };
};

const getRespondentRowTime = (row: any): number => {
  const candidates = [
    row?.update_dt,
    row?.updateDt,
    row?.updated_at,
    row?.updatedAt,
    row?.create_dt,
    row?.createDt,
    row?.created_at,
    row?.createdAt,
  ];

  for (const value of candidates) {
    const t = value ? new Date(value).getTime() : NaN;
    if (Number.isFinite(t)) return t;
  }
  return 0;
};

const sortRespondentRowsByRecency = (rows: any[]): any[] =>
  [...(rows ?? [])].sort((a, b) => {
    const diff = getRespondentRowTime(b) - getRespondentRowTime(a);
    if (diff !== 0) return diff;

    const wrA = Number((a as any)?.wr_id ?? (a as any)?.wrId ?? 0);
    const wrB = Number((b as any)?.wr_id ?? (b as any)?.wrId ?? 0);
    return wrB - wrA;
  });

const getRowTeamId = (row: any): string =>
  norm((row as any)?.team ?? (row as any)?.team_id ?? (row as any)?.teamId ?? '');

const uniqueNormNames = (arr: any[]): string[] => {
  const out: string[] = [];
  const seen = new Set<string>();

  (arr ?? []).forEach((item) => {
    const value = norm(item).replace(/\s+/g, ' ').trim();
    const key = coAuthorNameKey(value);
    if (!value || !key || seen.has(key)) return;
    seen.add(key);
    out.push(value);
  });

  return out;
};

export const parseRespondentNames = (raw: any): string[] => {
  const parse = (value: any): string[] => {
    if (value == null) return [];

    if (typeof value === 'string' || typeof value === 'number') {
      const s = norm(value);
      if (!s) return [];

      const looksJson = (s.startsWith('[') && s.endsWith(']')) || (s.startsWith('{') && s.endsWith('}'));
      if (looksJson) {
        try {
          return parse(JSON.parse(s));
        } catch {
          // Fall through to separator parsing.
        }
      }

      return s
        .split(RESPONDENT_SPLIT)
        .map((x) => norm(x))
        .filter(Boolean);
    }

    if (Array.isArray(value)) {
      return value.flatMap((x) => parse(x));
    }

    if (typeof value === 'object') {
      const direct = optionFromAny(value);
      if (direct) return [direct.label || direct.value];
      return Object.values(value).flatMap((x) => parse(x));
    }

    return [];
  };

  return uniqueNormNames(parse(raw));
};

const buildRespondentOptionsFromRows = (rows: any[]): CoAuthorOption[] =>
  mergeCoAuthorOptionsByLabel(
    uniqueNormNames(
      sortRespondentRowsByRecency(rows).flatMap((row) => parseRespondentNames((row as any)?.respondents))
    ).map((name) => ({ value: name, label: name }))
  );

const buildUserRespondentOptions = (rows: any[], loginKey: string): CoAuthorOption[] => {
  const key = norm(loginKey).toLowerCase();
  if (!key) return [];

  return buildRespondentOptionsFromRows(
    sortRespondentRowsByRecency(rows).filter((row) => {
      const created = norm((row as any)?.create_by_user_id ?? '').toLowerCase();
      const updated = norm((row as any)?.update_by_user_id ?? '').toLowerCase();
      return created === key || updated === key;
    })
  );
};

const buildTeamRespondentOptions = (rows: any[], teamIds: string[]): CoAuthorOption[] => {
  const teamSet = new Set((teamIds ?? []).map((x) => norm(x)).filter(Boolean));
  if (teamSet.size === 0) return [];

  return buildRespondentOptionsFromRows(
    sortRespondentRowsByRecency(rows).filter((row) => teamSet.has(getRowTeamId(row)))
  );
};

const fetchRows = async (params: URLSearchParams): Promise<any[]> => {
  const resp = await apiClient.get('/weekly-report-search', { params });
  return Array.isArray(resp.data) ? resp.data : [];
};

const fetchRespondentOptions = async (loginKey: string, teamIds: string[]): Promise<CoAuthorOption[]> => {
  const [teamRows, userRows] = await Promise.all([
    (async () => {
      if (teamIds.length === 0) return [];
      const teamParams = new URLSearchParams();
      teamIds.forEach((id) => teamParams.append('teams', id));
      return fetchRows(teamParams);
    })(),
    (async () => {
      if (!loginKey) return [];
      const userParams = new URLSearchParams();
      userParams.set('create_by_user_id', loginKey);
      userParams.set('update_by_user_id', loginKey);
      return fetchRows(userParams);
    })(),
  ]);

  return mergeCoAuthorOptionsByLabel(
    buildTeamRespondentOptions(teamRows, teamIds),
    buildUserRespondentOptions(userRows, loginKey)
  );
};

const refreshCache = (cacheKey: string, loginKey: string, teamIds: string[]): Promise<CoAuthorOption[]> => {
  const existing = inFlight.get(cacheKey);
  if (existing) return existing;

  const request = fetchRespondentOptions(loginKey, teamIds)
    .then((remoteOptions) => {
      const currentOptions = readCache(cacheKey)?.options ?? [];
      const merged = limitOptions(mergeCoAuthorOptionsByLabel(remoteOptions, currentOptions));
      writeCache(cacheKey, merged);
      return merged;
    })
    .finally(() => {
      inFlight.delete(cacheKey);
    });

  inFlight.set(cacheKey, request);
  return request;
};

const isCacheFresh = (payload: CachePayload | null): boolean =>
  !!payload && Date.now() - payload.savedAt < CACHE_TTL_MS;

const buildOfficialKeySet = (officialOptions: HookParams['officialOptions']): Set<string> => {
  const keys = new Set<string>();

  mergeCoAuthorOptionsByLabel((officialOptions ?? []) as any[]).forEach((option) => {
    const value = norm(option.value);
    const label = norm(option.label);
    const nameKey = coAuthorNameKey(label || value);

    if (value) keys.add(`v:${value}`);
    if (label) keys.add(`l:${label}`);
    if (nameKey) keys.add(`k:${nameKey}`);
  });

  return keys;
};

const filterCustomOptions = (
  respondentOptions: CoAuthorOption[],
  officialOptions: HookParams['officialOptions']
): CoAuthorOption[] => {
  const officialKeys = buildOfficialKeySet(officialOptions);

  return limitOptions(respondentOptions).filter((option) => {
    const value = norm(option.value);
    const label = norm(option.label);
    const nameKey = coAuthorNameKey(label || value);
    return !(
      (value && officialKeys.has(`v:${value}`)) ||
      (label && officialKeys.has(`l:${label}`)) ||
      (nameKey && officialKeys.has(`k:${nameKey}`))
    );
  });
};

export const useCoAuthorRespondentOptions = ({
  loginId,
  departmentId,
  teamIds,
  officialOptions = [],
}: HookParams) => {
  const cacheKey = useMemo(
    () => makeCacheKey({ loginId, departmentId, teamIds }),
    [loginId, departmentId, teamIds]
  );
  const loginKey = useMemo(() => norm(loginId).toLowerCase(), [loginId]);
  const normalizedTeamIds = useMemo(() => normalizeTeamIds(teamIds), [teamIds]);

  const [respondentOptions, setRespondentOptions] = useState<CoAuthorOption[]>(
    () => readCache(cacheKey)?.options ?? []
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const syncFromCache = () => {
      setRespondentOptions(readCache(cacheKey)?.options ?? []);
    };

    syncFromCache();
    return subscribe(cacheKey, syncFromCache);
  }, [cacheKey]);

  useEffect(() => {
    const cached = readCache(cacheKey);
    const canFetch = !!loginKey || normalizedTeamIds.length > 0;

    if (!canFetch || isCacheFresh(cached)) {
      setIsRefreshing(false);
      return;
    }

    let alive = true;
    setIsRefreshing(true);

    refreshCache(cacheKey, loginKey, normalizedTeamIds)
      .catch(() => {
        // Keep stale cached values visible when refresh fails.
      })
      .finally(() => {
        if (alive) setIsRefreshing(false);
      });

    return () => {
      alive = false;
    };
  }, [cacheKey, loginKey, normalizedTeamIds]);

  const addOptions = useCallback(
    (nextOptions: Array<CoAuthorOption | string | number | null | undefined>) => {
      const additions = mergeCoAuthorOptionsByLabel(nextOptions);
      if (additions.length === 0) return;

      const current = readCache(cacheKey)?.options ?? [];
      writeCache(cacheKey, mergeCoAuthorOptionsByLabel(additions, current));
    },
    [cacheKey]
  );

  const customOptions = useMemo(
    () => filterCustomOptions(respondentOptions, officialOptions),
    [respondentOptions, officialOptions]
  );

  return { respondentOptions, customOptions, isRefreshing, addOptions };
};
